import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute, PublicRoute } from "@/components/ProtectedRoute";
import { BugReportButton } from "@/components/BugReportButton";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import SparkBalanceWidget from "@/components/SparkBalanceWidget";

// Pages
import Dashboard from "@/pages/Dashboard";
import ProjectView from "@/pages/ProjectView";
import ProjectEditor from "@/pages/ProjectEditor";
import AnimationStudio from "@/pages/AnimationStudio";
import Storyboard from "@/pages/Storyboard";
import ScriptTools from "@/pages/ScriptTools";
import Characters from "@/pages/Characters";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import SetupAccount from "@/pages/SetupAccount";
import SuperAdmin from "@/pages/SuperAdmin";
import Account from "@/pages/Account";
import ActorPortal from "@/pages/ActorPortal";
import TeamManagement from "@/pages/TeamManagement";
import HelpDesk from "@/pages/HelpDesk";
import MotionLab from "@/pages/MotionLab";
import MotionLabV3 from "@/pages/MotionLabV3";
import PuppetLab from "@/pages/PuppetLab";
import Lab3D from "@/pages/Lab3D";
import Studio3D from "@/pages/Studio3D";
import AnimationStudioNew from "@/pages/AnimationStudioNew";
import SeedanceTest from "@/pages/SeedanceTest";
import Kling3Test from "@/pages/Kling3Test";
import DreamactorTest from "@/pages/DreamactorTest";
import FrameRestyler from "@/pages/FrameRestyler";
import ToonCrafterTest from "@/pages/ToonCrafterTest";
import ConsistentCharTest from "@/pages/ConsistentCharTest";

function App() {
  return (
    <div className="app-container">
      <div className="noise-overlay" />
      <AuthProvider>
        <ImpersonationBanner />
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/setup-account" element={<SetupAccount />} />
            <Route path="/help" element={<HelpDesk />} />

            {/* Protected routes - Director & Super Admin */}
            <Route path="/" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <ProjectView />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId/storyboard" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <Storyboard />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId/script-tools" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <ScriptTools />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId/characters" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <Characters />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId/team" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <TeamManagement />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId/editor" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <ProjectEditor />
              </ProtectedRoute>
            } />
            <Route path="/project/:projectId/studio/:sceneId" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <AnimationStudio />
              </ProtectedRoute>
            } />

            {/* Super Admin only */}
            <Route path="/super-admin" element={
              <ProtectedRoute roles={["super_admin"]}>
                <SuperAdmin />
              </ProtectedRoute>
            } />

            {/* Actor Portal - Actor only */}
            <Route path="/actor-portal" element={
              <ProtectedRoute roles={["actor"]}>
                <ActorPortal />
              </ProtectedRoute>
            } />

            {/* Account - all authenticated users */}
            <Route path="/account" element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            } />

            {/* Motion Lab - Experimental */}
            <Route path="/motion-lab" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <MotionLab />
              </ProtectedRoute>
            } />

            {/* Motion Lab V3 - Stick Figure Guided */}
            <Route path="/motion-lab-v3" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <MotionLabV3 />
              </ProtectedRoute>
            } />
            
            {/* Puppet Lab - 2D Puppet Animation */}
            <Route path="/puppet-lab" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <PuppetLab />
              </ProtectedRoute>
            } />
            
            {/* 3D Lab - Convert 2D to 3D */}
            <Route path="/3d-lab" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <Lab3D />
              </ProtectedRoute>
            } />
            
            {/* Animation Studio - 3D Character Animation Editor */}
            <Route path="/animation-studio" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <AnimationStudioNew />
              </ProtectedRoute>
            } />
            
            {/* Seedance Test - Secret test page for Seedance 2.0 evaluation */}
            <Route path="/seedance-test" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <SeedanceTest />
              </ProtectedRoute>
            } />
            
            {/* Kling 3.0 Test - Secret test page for Kling 3.0 via fal.ai */}
            <Route path="/kling3-test" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <Kling3Test />
              </ProtectedRoute>
            } />
            
            {/* Dreamactor V2 Test - Secret test page for puppet-style 2D animation */}
            <Route path="/dreamactor-test" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <DreamactorTest />
              </ProtectedRoute>
            } />
            
            {/* Frame Restyler - Post-process videos to clean 2D cartoon style */}
            <Route path="/frame-restyler" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <FrameRestyler />
              </ProtectedRoute>
            } />
            
            {/* ToonCrafter Test - 2D cartoon animation interpolation */}
            <Route path="/tooncrafter-test" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <ToonCrafterTest />
              </ProtectedRoute>
            } />
            
            {/* Consistent Character Test - Generate consistent character poses */}
            <Route path="/consistent-char-test" element={
              <ProtectedRoute roles={["director", "super_admin"]}>
                <ConsistentCharTest />
              </ProtectedRoute>
            } />
          </Routes>
          <SparkBalanceWidget />
        </BrowserRouter>
        <Toaster position="bottom-right" />
        <BugReportButton />
      </AuthProvider>
    </div>
  );
}

export default App;
